import React from 'react';
import { useTranslation } from 'react-i18next';
import { COMMISSION_TIER_TYPE_OPTIONS } from '../../../config/enum-options.js';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import CommissionRecalculationModal from '../../commission-recalculation/ui/commission-recalculation-modal.jsx';

function CommissionPlanDetailsPageView({
  form, isCreateMode, isLoading, isSaving, error, success,
  showCommissionRecalculationModal, commissionRecalculationError,
  onFormChange, onTierChange, addTier, removeTier, saveForm, navigateBack,
  openCommissionRecalculationModal, closeCommissionRecalculationModal, submitCommissionRecalculation,
}) {
  const { t } = useTranslation();
  const title = isCreateMode ? t('commissionPlans.createTitle') : t('commissionPlans.editTitle');
  const subtitle = isCreateMode ? t('commissionPlans.createSubtitle') : form.name || t('commissionPlans.title');

  return (
    <div>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={title} subtitle={subtitle} />
      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData className="">
            <form onSubmit={saveForm}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">{t('commissionPlans.planName')}</label>
                  <input type="text" className="form-control" value={form.name}
                    onChange={(e) => onFormChange('name', e.target.value)} disabled={isSaving} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('commissionPlans.maxDepth')}</label>
                  <input type="number" className="form-control" value={form.maxDepth}
                    onChange={(e) => onFormChange('maxDepth', parseInt(e.target.value, 10) || 0)} min="1" disabled={isSaving} />
                </div>
                <div className="col-md-3 d-flex align-items-end gap-3">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="isDefault" checked={form.isDefault}
                      onChange={(e) => onFormChange('isDefault', e.target.checked)} disabled={isSaving} />
                    <label className="form-check-label" htmlFor="isDefault">{t('commissionPlans.default')}</label>
                  </div>
                  {!isCreateMode ? (
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="isActive" checked={form.isActive}
                        onChange={(e) => onFormChange('isActive', e.target.checked)} disabled={isSaving} />
                      <label className="form-check-label" htmlFor="isActive">{t('commissionPlans.active')}</label>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-4">
                <section aria-labelledby="commission-plan-tiers-title">
                  <h5 id="commission-plan-tiers-title" className="mb-3">
                    {t('commissionPlans.commissionTiers')}
                  </h5>
                  {form.tiers.length === 0 ? (
                    <div className="text-muted mb-3">{t('commissionPlans.noTiers')}</div>
                  ) : (
                    <table className="table align-middle text-nowrap mb-3">
                      <thead className="header-item">
                        <tr>
                          <th>{t('commissionPlans.tierDepth')}</th>
                          <th>{t('commissionPlans.tierType')}</th>
                          <th>{t('commissionPlans.tierAmount')}</th>
                          <th>{t('commissionPlans.tierCurrency')}</th>
                          <th>{t('commissionPlans.tierMinLots')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.tiers.map((tier) => (
                          <tr key={tier.key}>
                            <td style={{ width: '100px' }}>
                              <input type="number" className="form-control form-control-sm" value={tier.depth}
                                onChange={(e) => onTierChange(tier.key, 'depth', parseInt(e.target.value, 10) || 0)} min="0" disabled={isSaving} />
                            </td>
                            <td style={{ width: '160px' }}>
                              <select className="form-select form-select-sm" value={tier.type}
                                onChange={(e) => onTierChange(tier.key, 'type', e.target.value)} disabled={isSaving}>
                                {COMMISSION_TIER_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ width: '140px' }}>
                              <input type="number" className="form-control form-control-sm" value={tier.amount}
                                onChange={(e) => onTierChange(tier.key, 'amount', e.target.value)} min="0" step="0.000001" disabled={isSaving} />
                            </td>
                            <td style={{ width: '100px' }}>
                              <input type="text" className="form-control form-control-sm" value={tier.currency}
                                onChange={(e) => onTierChange(tier.key, 'currency', e.target.value)} disabled={isSaving} />
                            </td>
                            <td style={{ width: '120px' }}>
                              <input type="text" className="form-control form-control-sm" value={tier.minLots}
                                onChange={(e) => onTierChange(tier.key, 'minLots', e.target.value)} placeholder={t('commissionPlans.optional')} disabled={isSaving} />
                            </td>
                            <td style={{ width: '50px' }}>
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeTier(tier.key)} disabled={isSaving}>
                                <i className="ti ti-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTier} disabled={isSaving}>
                    <i className="ti ti-plus me-1"></i>{t('commissionPlans.addTier')}
                  </button>
                </section>
              </div>
              <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-4">
                <div>
                  {!isCreateMode ? (
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={openCommissionRecalculationModal} disabled={isSaving}>
                      <i className="ti ti-refresh me-1"></i>{t('commissionPlans.recalculateCommissions')}
                    </button>
                  ) : null}
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-default" onClick={navigateBack} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading || isSaving}>
                    {isCreateMode ? t('commissionPlans.createPlan') : t('commissionPlans.saveChanges')}
                  </button>
                </div>
              </div>
            </form>
          </LoadingContainer>
        </div>
      </div>
      {!isCreateMode ? (
        <CommissionRecalculationModal
          isOpen={showCommissionRecalculationModal}
          isSaving={isSaving}
          title={t('commissionPlans.recalculateCommissionsTitle')}
          scopeLabel={t('commissionPlans.recalculateCommissionsScope', { name: form.name || t('commissionPlans.title') })}
          error={commissionRecalculationError}
          onSubmit={submitCommissionRecalculation}
          onClose={closeCommissionRecalculationModal}
        />
      ) : null}
    </div>
  );
}

export default CommissionPlanDetailsPageView;
